// src/components/kuestion/campus/BulkImport.jsx
//
// CSV import (contract §5.1). The file is validated client-side first so the
// examiner sees row errors before a round trip; the server validates again
// and rejects the file as a unit if anything is wrong — no partial imports.
// On success the created logins and generated passwords are shown once and
// can be downloaded, because that screen is the only chance to capture them.
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { copyForReason } from '../useKuestionApi';
import { Alert, Note, CopyButton } from '../shared/Bits';
import { UploadIcon, LockIcon, WarnIcon, CheckIcon, DownloadIcon } from '../shared/Icons';
import { passwordProblems, cleanStudentCode, STUDENT_CODE } from '../shared/password';
import { downloadBlob, toCsv } from '../shared/format';
import { kq } from '../../../config/routes';

const COLS = ['student_code', 'first_name', 'last_name', 'password'];

// Minimal RFC-4180 parser: quoted fields, doubled quotes, CRLF or LF.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQ = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else inQ = false;
      } else cell += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((v) => v.trim() !== ''));
}

export default function BulkImport() {
  const navigate = useNavigate();
  const { org, api, students, reloadStudents } = useCampus();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);       // parsed data rows with line numbers
  const [parseError, setParseError] = useState('');
  const [serverErrors, setServerErrors] = useState(null); // Map line -> message
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [over, setOver] = useState(false);
  const [result, setResult] = useState(null); // created students

  const existing = useMemo(() => new Set(students.map((s) => String(s.student_code).toUpperCase())), [students]);

  const load = async (f) => {
    setFile(f); setRows([]); setParseError(''); setServerErrors(null); setError(''); setResult(null);
    if (!f) return;
    let text;
    try { text = await f.text(); } catch { setParseError('The file could not be read.'); return; }
    const all = parseCsv(text);
    if (!all.length) { setParseError('The file is empty.'); return; }
    let header = all[0].map((h) => h.trim().toLowerCase());
    let start = 1;
    const hasHeader = header.includes('student_code') || header.includes('first_name');
    if (!hasHeader) { header = COLS; start = 0; }
    const idx = (name) => header.indexOf(name);
    const ci = idx('student_code'); const fi = idx('first_name'); const li = idx('last_name'); const pi = idx('password');
    if (ci < 0 || fi < 0 || li < 0) { setParseError('The header must include student_code, first_name and last_name.'); return; }
    setRows(all.slice(start).map((r, n) => ({
      line: start + n + 1,
      code: (r[ci] || '').trim(),
      first: (r[fi] || '').trim(),
      last: (r[li] || '').trim(),
      password: pi >= 0 ? (r[pi] || '') : '',
    })));
  };

  // Client-side checks, same rules the server applies.
  const checked = useMemo(() => {
    const seen = new Map();
    return rows.map((r) => {
      const problems = [];
      const code = cleanStudentCode(r.code);
      if (!STUDENT_CODE.test(code) || code !== r.code.toUpperCase()) problems.push('Code must be exactly 6 letters or numbers');
      else if (existing.has(code)) problems.push('Code already exists in your organisation');
      else if (seen.has(code)) problems.push(`Duplicate code — already used on row ${seen.get(code)}`);
      else seen.set(code, r.line);
      if (!r.first) problems.push('First name is required');
      if (!r.last) problems.push('Last name is required');
      if (r.password) {
        const p = passwordProblems(r.password);
        if (p.length) problems.push(`Password needs ${p.join(', ')}`);
      }
      const server = serverErrors?.get(r.line);
      if (server) problems.push(server);
      return { ...r, problems };
    });
  }, [rows, existing, serverErrors]);

  const bad = checked.filter((r) => r.problems.length);
  const canUpload = file && rows.length > 0 && bad.length === 0 && !busy;

  const upload = async () => {
    if (!canUpload) return;
    setBusy(true); setError(''); setServerErrors(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const data = await api.postForm(API_ENDPOINTS.CAMPUS_STUDENTS_BULK, fd);
      const created = Array.isArray(data) ? data : (data?.created || data?.students || []);
      setResult(created.map((s) => ({ ...s, password: s.generated_password || s.password || '' })));
      reloadStudents();
    } catch (e) {
      // `validation_failed` carries errors[] of {row, reason} (row numbers are
      // 1-based including the header, matching the preview) plus error_count,
      // capped at 100 entries. Row reasons use the single-student codes.
      const body = e.body || {};
      const list = body.errors || body.rows || body.data?.errors || body.data?.rows;
      if (Array.isArray(list) && list.length) {
        const m = new Map();
        list.forEach((it) => {
          const line = Number(it.row ?? it.line);
          const reasons = Array.isArray(it.errors) ? it.errors : [it.reason || it.error || it.message || 'invalid'];
          const msg = reasons.map((r) => copyForReason(String(r), String(r).replace(/_/g, ' '))).join(' · ');
          if (Number.isFinite(line)) m.set(line, msg);
        });
        setServerErrors(m);
        const count = body.error_count ?? m.size;
        setError(`${count} row${count === 1 ? ' is' : 's are'} invalid, so nothing was imported.${count > m.size ? ` The first ${m.size} are marked below.` : ''}`);
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => downloadBlob(toCsv([COLS, ['A7K2M9', 'Ayesha', 'Khan', ''], ['B3P8L1', 'Marcus', 'Oyelaran', '']]), 'kuestion-students-template.csv', 'text/csv');
  const downloadResult = () => downloadBlob(toCsv([['login_id', 'first_name', 'last_name', 'password'], ...result.map((s) => [s.login_id, s.first_name, s.last_name, s.password])]), `${org?.prefix || 'students'}-logins.csv`, 'text/csv');

  const onDrop = (e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) load(f); };

  // Step state for the bar
  const step = result ? 3 : (rows.length && bad.length) ? 2 : 1;

  return (
    <div className="kqc-page kqc-page--mid">
      <button type="button" className="btn btn-ghost kqc-back" onClick={() => navigate(kq('students'))}>&larr; Students</button>
      <div className="kqc-pagehead" style={{ marginBottom: 8 }}>
        <h1 className="kqc-h1">Import a class</h1>
        <span className="kqc-spacer" />
        <button type="button" className="btn btn-secondary btn-sm" onClick={downloadTemplate}><DownloadIcon />Download the template CSV</button>
      </div>
      <p className="kqc-lede" style={{ marginBottom: 26 }}>Four columns: <strong>student_code</strong>, <strong>first_name</strong>, <strong>last_name</strong> and an optional <strong>password</strong>. Leave the password blank — that is the normal path — and the server generates a compliant one.</p>

      <div className="kqc-steps" style={{ marginBottom: 26 }}>
        <div className={`kqc-step${step === 1 ? ' is-current' : ' is-done'}`}><small>Step 1</small><strong>Upload</strong></div>
        <div className={`kqc-step${step === 2 ? ' is-alert' : step > 2 ? ' is-done' : ''}`}><small>Step 2</small><strong>{bad.length ? `Fix ${bad.length} error${bad.length === 1 ? '' : 's'}` : 'Check the rows'}</strong></div>
        <div className={`kqc-step${step === 3 ? ' is-current' : ''}`}><small>Step 3</small><strong>Capture passwords</strong></div>
      </div>

      {result ? (
        <>
          <div className="kqc-box kqc-box--accent" style={{ marginBottom: 24 }}>
            <div className="kqc-box__head kqc-box__head--accent"><CheckIcon /><span>{result.length} student{result.length === 1 ? '' : 's'} created</span><span className="kqc-spacer" /><span>{file?.name}</span></div>
            <div className="kqc-box__body kqc-small" style={{ color: 'var(--color-neutral-800)' }}>
              <strong>This is your only chance to capture the passwords.</strong> They are never retrievable afterwards, only resettable one student at a time. Download the CSV or copy the list before leaving this page.
            </div>
          </div>
          <div className="kqc-row" style={{ marginBottom: 18 }}>
            <button type="button" className="btn btn-primary" onClick={downloadResult}><DownloadIcon />Download logins CSV</button>
            <CopyButton text={result.map((s) => `${s.login_id}\t${s.first_name} ${s.last_name}\t${s.password}`).join('\n')} label="Copy all" ink />
            <button type="button" className="btn btn-secondary" onClick={() => navigate(kq('students'))}>Go to the roster</button>
          </div>
          <div className="table-wrap">
            <table className="table" style={{ minWidth: 640 }}>
              <thead><tr><th>Login ID</th><th>Name</th><th>Password</th></tr></thead>
              <tbody>
                {result.map((s, i) => (
                  <tr key={s.id ?? i}>
                    <td><span className="kqc-loginid">{s.login_id}</span></td>
                    <td>{s.first_name} {s.last_name}</td>
                    <td className="kqc-mono">{s.password || <span className="kqc-muted">set by you</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div
            className={`kqc-drop${over ? ' is-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current && fileRef.current.click()}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current.click(); }}
            style={{ marginBottom: 24 }}
          >
            <span className="i"><UploadIcon /></span>
            <span>
              <strong>{file ? file.name : 'Drop a CSV here, or click to choose'}</strong>
              <em>{file ? `${rows.length} row${rows.length === 1 ? '' : 's'} read — ${bad.length ? `${bad.length} need fixing` : 'all ready'}` : 'UTF-8, comma separated. A header row is expected.'}</em>
            </span>
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => load(e.target.files && e.target.files[0])} />
          </div>

          {parseError && <Alert kind="error">{parseError}</Alert>}
          {error && <Alert kind="error" onClose={() => setError('')}>{error}</Alert>}

          {rows.length > 0 && (
            <>
              {bad.length > 0 && (
                <div className="kqc-box kqc-box--danger" style={{ marginBottom: 24 }}>
                  <div className="kqc-box__head kqc-box__head--danger"><WarnIcon /><span>Nothing will be imported — {bad.length} of {rows.length} rows {bad.length === 1 ? 'is' : 'are'} invalid</span><span className="kqc-spacer" /><span>{file?.name}</span></div>
                  <div className="kqc-box__body kqc-small" style={{ color: 'var(--color-neutral-800)' }}>The file is accepted or rejected as one unit — there are no partial imports, so your roster stays exactly as it is. Correct the rows below in the file and upload it again.</div>
                </div>
              )}
              <div className="table-wrap" style={{ marginBottom: 26 }}>
                <table className="table" style={{ minWidth: 720 }}>
                  <thead><tr><th style={{ width: 52 }}>Row</th><th>student_code</th><th>first_name</th><th>last_name</th><th>password</th><th>Result</th></tr></thead>
                  <tbody>
                    {checked.map((r) => (
                      <tr key={r.line} className={r.problems.length ? 'is-bad' : ''}>
                        <td>{r.line}</td>
                        <td>{r.problems.length ? <strong>{r.code || '—'}</strong> : `${org?.prefix}-${r.code.toUpperCase()}`}</td>
                        <td>{r.first || '—'}</td>
                        <td>{r.last || '—'}</td>
                        <td className="kqc-muted">{r.password ? 'set' : 'auto'}</td>
                        <td>{r.problems.length ? <span className="tag tag-outline" style={{ whiteSpace: 'normal' }}>{r.problems.join(' · ')}</span> : <span className="tag tag-neutral">Ready</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="kqc-box kqc-box--surface" style={{ maxWidth: 720 }}>
            <div className="kqc-box__body kqc-box__body--pad">
              <div className="kqc-row" style={{ marginBottom: 10 }}>
                <span style={{ display: 'flex', width: 26, height: 26, color: 'var(--color-accent)' }}><LockIcon /></span>
                <span className="kqc-mono" style={{ fontSize: 16 }}>What happens after a clean upload</span>
              </div>
              <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 14 }}>Every created student appears once with their full login ID and generated password, downloadable as a CSV. <strong>That screen is your only chance to capture them</strong> — passwords are never retrievable afterwards, only resettable.</p>
              <div className="kqc-row">
                <button type="button" className="btn btn-primary" disabled={!canUpload} onClick={upload}>{busy ? 'Importing…' : rows.length ? `Import ${rows.length} student${rows.length === 1 ? '' : 's'}` : 'Upload a file first'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(kq('students'))} disabled={busy}>Cancel import</button>
              </div>
            </div>
          </div>
          {!rows.length && <Note style={{ marginTop: 18 }}>Codes are upper-cased on import. Blank passwords are generated by the server and returned once.</Note>}
        </>
      )}
    </div>
  );
}
